import { type FC, useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { useDebounce } from "@/shared/hooks";
import { useAutocompleteProductsQuery } from "../hooks/useAutocompleteProductsQuery";
import {
  mergeProductLabels,
  PRODUCT_SEARCH_DEBOUNCE_MS,
  type ProductOption,
} from "../model/productLabels";
import {
  useProductLabels,
  useRememberProductLabels,
} from "../hooks/useProductLabels";

interface ProductsAutocompleteProps {
  label: string;
  value: string[];
  onChange: (productIds: string[]) => void;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  /** Предел числа выбранных: дальше варианты недоступны (проверяет и сервер). */
  max?: number;
}

/**
 * Тот же виджет, что и ProductsField (мультиселект по autocompleteProducts),
 * но не завязан на react-final-form: управляемый компонент value/onChange.
 * Используется в фильтрах списков (VideosFilter/CollectionsFilter/
 * StreamsFilter), в StreamForm, промокодах и в условиях маркетинга.
 *
 * Запросы (супер-ревью кабинета 9): варианты спрашиваются только у
 * открытого списка и с паузой после ввода (одинаковый поиск у всех полей
 * даёт одни переменные и один ответ из кеша); названия выбранных продуктов
 * берутся из ProductLabelsProvider (один запрос на редактор), без него
 * своим запросом по id, названия которых поле не знает. Выбор варианта
 * запроса не даёт: название пришло вместе с вариантом.
 */
export const ProductsAutocomplete: FC<ProductsAutocompleteProps> = ({
  label,
  value,
  onChange,
  disabled,
  error,
  helperText,
  max,
}) => {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState<ReadonlyMap<string, string>>(
    () => new Map()
  );
  const debouncedSearch = useDebounce(search, PRODUCT_SEARCH_DEBOUNCE_MS);
  const remember = useRememberProductLabels();
  const { data, loading } = useAutocompleteProductsQuery({
    fetchPolicy: "cache-and-network",
    variables: { input: debouncedSearch || undefined },
    skip: !open,
  });
  const labels = useProductLabels(value, picked);

  const selected = value.flatMap((id) => {
    const name = labels.get(id);
    return name === undefined ? [] : [{ value: id, label: name }];
  });
  const optionsById = new Map<string, ProductOption>(
    selected.map((option) => [option.value, option])
  );
  if (open) {
    for (const option of data?.autocompleteProducts?.options ?? []) {
      if (option) optionsById.set(option.value, option);
    }
  }
  const options = Array.from(optionsById.values());

  return (
    <Autocomplete<ProductOption, true>
      multiple
      fullWidth
      disabled={disabled}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      filterOptions={(opts) => opts}
      loading={open && (loading || search !== debouncedSearch)}
      options={options}
      getOptionLabel={(option) => option.label}
      getOptionDisabled={(option) =>
        max !== undefined &&
        value.length >= max &&
        !value.includes(option.value)
      }
      isOptionEqualToValue={(option, chosen) => option.value === chosen.value}
      value={selected}
      onInputChange={(_event, newInputValue, reason) => {
        setSearch(reason === "reset" ? "" : newInputValue);
      }}
      onChange={(_event, newValue) => {
        setPicked((prev) => mergeProductLabels(prev, newValue));
        remember?.(newValue);
        onChange(newValue.map((option) => option.value));
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          error={error}
          helperText={helperText}
        />
      )}
    />
  );
};
